import { useEffect, useRef, useState } from "react";

interface ContainerSize {
  width: number;
  height: number;
}

type UseMeasureReturn<T extends HTMLElement> = {
  ref: React.RefObject<T>;
  size: ContainerSize;
  windowSize: ContainerSize;
};

const initSize: ContainerSize = { width: 0, height: 0 };

const useMeasure = <
  T extends HTMLElement = HTMLDivElement,
>(): UseMeasureReturn<T> => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ContainerSize>(initSize);
  const [windowSize, setWindowSize] = useState<ContainerSize>(initSize);

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(ref.current);

    return (): void => {
      observer.disconnect();
    };
  }, []);

  // Measure window size with event listener
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial size
    handleResize();

    window.addEventListener("resize", handleResize);

    return (): void => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return { ref, size, windowSize };
};

export default useMeasure;
